import React from 'react';
import PropTypes from 'prop-types';
import { ApolloClient, ApolloLink, ApolloProvider, HttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { useAuth } from 'react-oidc-context';

const url = window.location.origin;

const getEnv = () => {
  switch (true) {
    case url.includes('one.cube.toyota.com'):
      return 'prod';
    case url.includes('one.cubeuat.toyota.com'):
      return 'uat';
    case url.includes('one.cubeqa.toyota.com'):
      return 'qa';
    case url.includes('one.cubeprf.toyota.com'):
      return 'prf';
    case url.includes('one.cubesit.toyota.com'):
      return 'sit';
    case url.includes('one.cubedev.toyota.com'):
    case url.includes('localhost'):
    default:
      return 'dev';
  }
};

const getBaseURL = () =>
  ({
    dev: 'one.cubedev.toyota.com',
    sit: 'one.cubesit.toyota.com',
    prf: 'one.cubeprf.toyota.com',
    qa: 'one.cubeqa.toyota.com',
    uat: 'one.cubeuat.toyota.com',
    prod: 'one.cube.toyota.com',
  }[getEnv()]);

const arrivalHttpLink = new HttpLink({
  uri: `https://api-arrival-mdb.${getBaseURL()}/graphql`,
});

const commonHttpLink = new HttpLink({
  uri: `https://api-common.${getBaseURL()}/graphql`,
});

const pipelineHttpLink = new HttpLink({
  uri: `https://api-pipeline-mdb.${getBaseURL()}/graphql`,
});

const tlsHttpLink = new HttpLink({ uri: `https://api-tlspipeline.${getBaseURL()}/graphql` });

const ApolloClientProvider = ({ children, defaultClient }) => {
  const { user } = useAuth();

  const authLink = setContext(() => ({
    headers: {
      Authorization: user.access_token,
    },
  }));

  // returns true if current 'client' is selected
  const getIsClient = (operation, client) => {
    const clientName = operation.getContext().clientName;
    return clientName === client || (!clientName && client === defaultClient);
  };

  const arrivalClientLink = ApolloLink.split(
    (operation) => getIsClient(operation, 'arrival'),
    authLink.concat(arrivalHttpLink),
  );

  const commonClientLink = ApolloLink.split(
    (operation) => getIsClient(operation, 'common'),
    authLink.concat(commonHttpLink),
  );

  const pipelineClientLink = ApolloLink.split(
    (operation) => getIsClient(operation, 'pipeline'),
    authLink.concat(pipelineHttpLink),
  );

  const tlsClientLink = ApolloLink.split(
    (operation) => getIsClient(operation, 'tls'),
    authLink.concat(tlsHttpLink),
  );

  const composedLink = ApolloLink.from([
    commonClientLink,
    pipelineClientLink,
    arrivalClientLink,
    tlsClientLink,
  ]);

  const apolloClient = new ApolloClient({
    link: composedLink,
    cache: new InMemoryCache({
      addTypename: false,
    }),
  });

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
};

ApolloClientProvider.propTypes = {
  children: PropTypes.node.isRequired,
  defaultClient: PropTypes.oneOf(['common', 'pipeline', 'arrival', 'tls']).isRequired,
};

export default ApolloClientProvider;
